import React, { forwardRef } from 'react';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { useTheme, useMediaQuery, Box, Typography, Skeleton } from '@mui/material';
import IconNoDataFound from '../../assets/icons/IconNoDataFound';

import IconSquareChevUp from '../../assets/icons/IconSquareChevUp';
import IconSquareChevDown from '../../assets/icons/IconSquareChevDown';

const Table = ({
  columns,
  data,
  enableSearch = false,
  enableGlobalFilterModes = false,
  enableFilters = false,
  enableColumnPinning = false,
  enableColumnActions = false,
  enableColumnFilters = false,
  enableColumnOrdering = false,
  enableDensityToggle = false,
  enableFullScreenToggle = false,
  enableHiding = false,
  enablePagination = false,
  enableGrouping = false,
  showGlobalFilter = false,
  showTopToolbar = false,
  showBottomToolbar = false,
  showRowsPerPage = false,
  showPaginationArrows = false,
  pinnedLeftColumns = [],
  globalFilter = '',
  onGlobalFilterChange = () => {},
  expandedRows = undefined,
  onExpandedChange = undefined,
  manualPagination = false,
  rowCount,
  pagination,
  isLoading,
  isLoadingMore,
  onPaginationChange,
  tablePaperStyle = {},
  tableContainerStyle = {},
  onScrollEnd,
  mrtRowExpandWidth = 36,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

  const hasAnyChildren = data.some(
    (row) => Array.isArray(row.childData) && row.childData.length > 0
  );

  const sharedTableOptions = {
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        boxShadow: 'none',
        backgroundColor: '#fff',
        padding: 0,
        borderRadius: '8px',
        ...tablePaperStyle,
        '& > .MuiBox-root': {
          backgroundColor: '#fff',
          boxShadow: 'none',
          minHeight: '3.1rem',
          ...(showRowsPerPage === false && {
            minHeight: 'inherit',
          }),
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        padding: 0,
        backgroundColor: '#fff',
        border: '1px solid #E7E6EA',
        borderRadius: '8px',
        ...tableContainerStyle,
      },
      onScroll: onScrollEnd,
    },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        transition: 'none !important',
        backgroundColor: '#F5F5F5',
        padding: '12px 8px !important',
        fontWeight: '600',
        ...(column.id === 'mrt-row-expand' && {
          width: mrtRowExpandWidth ?? '36px',
          minWidth: mrtRowExpandWidth ?? '36px',
          maxWidth: mrtRowExpandWidth ?? '36px',
          '& .MuiButtonBase-root': {
            margin: 0,
          },
        }),
      },
    }),
    muiTableHeadRowProps: {
      sx: {
        '& th': { backgroundColor: '#F5F5F5' },
      },
    },
    muiTableBodyCellProps: ({ cell, column }) => {
      const cellId = cell?.row?.original?.id?.toString();
      const backgroundColor = cell?.row?.original?.backgroundColor;
      const color = cell?.row?.original?.color;

      return {
        sx: {
          height: '54px',
          transition: 'none !important',
          padding: '8px',
          backgroundColor:
            cell.row.depth > 0 ? '#F5F5F5' : backgroundColor ? backgroundColor : '#fff',
          color: color ?? '#666666',
          maxWidth: `max(calc(var(--col-${column.id}-size) * 1px), 40px)`,
          whiteSpace: 'nowrap', // single line
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          ...(column.id === 'mrt-row-expand' && {
            width: mrtRowExpandWidth ?? '36px',
            minWidth: mrtRowExpandWidth ?? '36px',
            maxWidth: mrtRowExpandWidth ?? '36px',
            '& .MuiButtonBase-root': {
              margin: 0,
            },
          }),
        },
        children:
          isLoading || (isLoadingMore && cellId && cellId?.includes('skeleton')) ? (
            <Skeleton variant='rectangular' height={22} width='100%' />
          ) : undefined,

        title: typeof cell.getValue() === 'string' ? cell.getValue() : undefined, // native tooltip on hover
      };
    },
    muiTableBodyRowProps: ({ row }) => ({
      className: row.depth > 0 ? 'child-table' : '',
      sx: {
        height: '54px',
        transition: 'none !important',
        backgroundColor: row.depth > 0 ? '#F5F5F5' : '#fff',
        '&:hover': { backgroundColor: 'transparent' },
        '&:last-child td': { borderBottom: 0 },
        '&:hover td::after': { content: 'none' },
      },
    }),
    muiTableHeadProps: {
      sx: {
        transition: 'none !important',
        padding: '16px',
        backgroundColor: '#fff',
      },
    },
    muiTableBodyProps: {
      sx: {
        transition: 'none !important',
        backgroundColor: 'white',
      },
    },
    muiToolbarDropZoneProps: {
      ...(enableGrouping === false && {
        sx: {
          '& .Mui-ToolbarDropZone': {
            display: 'none',
          },
        },
      }),
    },
    muiTopToolbarProps: {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        ...(enableGrouping === false && {
          display: 'none',
        }),
      },
    },
    muiBottomToolbarProps: {
      sx: {
        display: 'flex',
        justifyContent: 'center',
        '& .MuiBox-root': {
          width: '100%',
          justifyContent: 'space-between',
        },
        '& .MuiTablePagination-root': {
          width: '100%',
          paddingRight: 0,
          paddingLeft: 0,
          paddingBottom: 0,
          flexDirection: { xs: 'column', sm: 'row' },
          '& .MuiFormLabel-root': {
            order: 2,
            color: '#666666',
          },
          '& .MuiSelect-select, select': {
            padding: '8px 12px',
            paddingRight: '32px !important',
            border: '1px solid #90A1B9',
            borderRadius: '8px',
          },
          '& .MuiNativeSelect-icon': {
            right: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
          },
        },
        '& .MuiTablePagination-root .MuiBox-root': {
          width: 'auto',
        },
        ...(enablePagination === false && {
          '& .MuiBox-root': {
            display: 'none',
          },
        }),
      },
    },
    muiTablePaginationProps: {
      sx: {
        '& .MuiTablePagination-root .MuiSelect-select': {
          paddingRight: '32px',
        },
        ...(showRowsPerPage === false && {
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select':
            {
              display: 'none',
            },
        }),
        ...(showPaginationArrows === false && {
          '& .MuiTablePagination-actions': {
            display: 'none',
          },
        }),
      },
    },
  };

  const ArrowRightIcon = forwardRef((props, ref) => {
    const { style, className, ...rest } = props || {};
    return (
      <span ref={ref} style={style} className={className}>
        <IconSquareChevUp width={24} height={24} {...rest} />
      </span>
    );
  });

  const ExpandMoreIcon = forwardRef((props, ref) => {
    const { style, className, ...rest } = props || {};
    return (
      <span ref={ref} style={style} className={className}>
        <IconSquareChevDown width={24} height={24} {...rest} />
      </span>
    );
  });

  const tableState = { globalFilter };
  if (expandedRows !== undefined) {
    tableState.expanded = expandedRows;
  }
  if (pagination) {
    tableState.pagination = pagination;
  }
  const tableOptions = {
    columns,
    data,
    state: tableState,
    onGlobalFilterChange,
    renderGlobalFilter: () => null,

    getSubRows: (row) => row.childData ?? [],
    enableExpanding: hasAnyChildren,
    enableExpandAll: hasAnyChildren,
    getRowCanExpand: (row) =>
      Array.isArray(row?.original?.childData) && row.original.childData.length > 0,

    sortingFns: {
      alphanumericTopLevel: (rowA, rowB, columnId) => {
        if (rowA.depth > 0 || rowB.depth > 0) return 0;
        const a = rowA.getValue(columnId);
        const b = rowB.getValue(columnId);
        return a > b ? 1 : a < b ? -1 : 0;
      },
    },

    icons: {
      ArrowRightIcon,
      ExpandMoreIcon,
      // ChevronRightIcon: ArrowRightIcon,
      // ChevronLeftIcon: ExpandMoreIcon,
    },

    displayColumnDefOptions: {
      'mrt-row-expand': {
        Header: '',
        size: 36,
        minSize: 36,
        maxSize: 36,
        Cell: ({ row }) => {
          if (!row.getCanExpand()) return null;
          return (
            <span
              onClick={row.getToggleExpandedHandler()}
              style={{ cursor: 'pointer', display: 'inline-flex' }}
            >
              {row.getIsExpanded() ? <ArrowRightIcon /> : <ExpandMoreIcon />}
            </span>
          );
        },
      },
    },

    renderColumnDef: ({ column }) => {
      if (column.id === 'mrt-row-expand') {
        return {
          Header: () => null,
          header: '',
        };
      }
      return undefined;
    },

    enableGlobalFilter: enableSearch,
    enableGlobalFilterModes: enableGlobalFilterModes,
    enableFilters: enableFilters,
    enableColumnFilters: enableColumnFilters,
    enableColumnPinning: enableColumnPinning,
    enableColumnActions: enableColumnActions,
    enableColumnOrdering: enableColumnOrdering,
    enableDensityToggle: enableDensityToggle,
    enableFullScreenToggle: enableFullScreenToggle,
    enableHiding: enableHiding,
    enableGrouping: enableGrouping,

    initialState: {
      density: 'compact',
      showGlobalFilter: showGlobalFilter,
      ...(isSmallScreen && {
        columnPinning: {
          left: hasAnyChildren ? ['mrt-row-expand', ...pinnedLeftColumns] : [...pinnedLeftColumns],
        },
      }),

      ...(hasAnyChildren === false && {
        columnVisibility: { 'mrt-row-expand': false },
      }),
    },

    paginationDisplayMode: enablePagination ? 'pages' : undefined,
    muiPaginationProps: {
      ...(enablePagination ? { rowsPerPageOptions: showRowsPerPage ? [10, 20, 30] : [] } : {}),
      showFirstButton: false,
      showLastButton: false,
    },

    renderTopToolbar: showTopToolbar ? undefined : () => null,
    renderBottomToolbar: showBottomToolbar ? undefined : () => null,

    positionToolbarAlertBanner: 'bottom',
    localization: {
      rowsPerPage: 'entries per page',
    },

    renderEmptyRowsFallback: () => (
      <Box
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
        flexDirection={'column'}
        flex={1}
        height={'55vh'}
        py={4}
        gap={1}
      >
        <IconNoDataFound width={90} height={120} />
        <Typography variant='subtitle1' fontWeight={600} color='secondary.main'>
          NO DATA FOUND
        </Typography>
      </Box>
    ),

    manualPagination: manualPagination,
    rowCount,
    onPaginationChange: onPaginationChange,

    ...sharedTableOptions,
  };

  if (typeof onExpandedChange === 'function' && expandedRows !== undefined) {
    tableOptions.onExpandedChange = onExpandedChange;
  }

  const tableInstance = useMaterialReactTable(tableOptions);

  return <MaterialReactTable table={tableInstance} />;
};

export default Table;
